/**
 * Notification slice — the bell, its badge, and the list behind it.
 *
 * The unread count comes DOWN FROM THE SERVER with the list rather than being counted
 * from `notifications` here. Only the newest page is loaded, so counting locally would
 * under-report the moment a mechanic has more unread than one page — and the badge is
 * the whole point of the feature.
 *
 * Read state is updated optimistically. Marking read is idempotent server-side and
 * failure-free from the user's point of view; making them wait for a round trip to see
 * a badge clear is worse than the rare case of it clearing during a failure.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    fetchNotificationsApi,
    markNotificationReadApi,
    markAllNotificationsReadApi,
} from '../../services/notificationApi';

export const fetchNotifications = createAsyncThunk(
    'notifications/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            return await fetchNotificationsApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to load notifications');
        }
    }
);

export const markNotificationRead = createAsyncThunk(
    'notifications/markRead',
    async (uuid, { rejectWithValue }) => {
        try {
            return await markNotificationReadApi(uuid);
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to mark as read');
        }
    }
);

export const markAllNotificationsRead = createAsyncThunk(
    'notifications/markAllRead',
    async (_, { rejectWithValue }) => {
        try {
            await markAllNotificationsReadApi();
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to mark all as read');
        }
    }
);

const initialState = {
    notifications: [],
    unreadCount: 0,
    loading: false,
    error: null,
};

const notificationSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        clearNotificationError: (state) => { state.error = null; },
        /**
         * A push arrived while the app was open. The service worker posts a message
         * rather than the app polling for it, so the badge moves the moment the
         * notification exists instead of up to a poll interval later.
         */
        notificationReceived: (state) => { state.unreadCount += 1; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNotifications.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                state.loading = false;
                state.notifications = action.payload?.notifications ?? [];
                state.unreadCount = action.payload?.unreadCount ?? 0;
            })
            .addCase(fetchNotifications.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(markNotificationRead.pending, (state, action) => {
                // Optimistic — see the file comment. `action.meta.arg` is the uuid.
                const target = state.notifications.find((n) => n.uuid === action.meta.arg);
                if (target && !target.readAt) {
                    target.readAt = new Date().toISOString();
                    // Never below zero: a double tap would otherwise drive the badge
                    // negative and it renders as "-1".
                    state.unreadCount = Math.max(0, state.unreadCount - 1);
                }
            })
            .addCase(markAllNotificationsRead.pending, (state) => {
                const now = new Date().toISOString();
                state.notifications.forEach((n) => { if (!n.readAt) n.readAt = now; });
                state.unreadCount = 0;
            });
    },
});

export const { clearNotificationError, notificationReceived } = notificationSlice.actions;

export const selectUnreadCount = (state) => state.notifications.unreadCount;

export default notificationSlice.reducer;
